import React, { useState, useEffect } from 'react';
import { devLog, devWarn, devError } from '../../../utils/devLogger';
import styled from 'styled-components';
import CmsPopup from '../../../components/CmsPopup';
import { CmsPopupBtnType } from '../../../components/CmsPopupBtn';
import CmsPopupBtn from '../../../components/CmsPopupBtn';
import Modal from '../../../components/Modal';
import { AppTextField } from '../../../customComponents/AppTextField';
import { toast } from 'react-toastify';
import { createPush } from '../../../lib/api/admin/adminApi';

interface NotificationData {
  no: number;
  index: number;
  title: string;
  body: string;
  createBy: string;
  createAt: string;
  adminName: string;
}

// 알림 폼 데이터 타입 (API 스펙에 맞춰 수정)
export interface NotificationFormData {
  title: string;
  body: string;
}

interface NotificationFormPopupProps {
  isOpen: boolean;
  notificationData?: NotificationData | null;
  onClose: () => void;
  onSent: () => void;
}

// Styled Components
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 0;
`;

const ReadOnlyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  
  & > * {
    flex: 1;
  }
`;

const NotificationFormPopup: React.FC<NotificationFormPopupProps> = ({
  isOpen,
  notificationData,
  onClose,
  onSent,
}) => {
  const [formData, setFormData] = useState<NotificationFormData>({
    title: '',
    body: ''
  });
  const [formErrors, setFormErrors] = useState<Partial<NotificationFormData>>({});
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // 모드 확인 (새 작성 or 상세보기)
  const isViewMode = !!notificationData;

  useEffect(() => {
    if (isOpen) {
      if (notificationData) {
        // 상세보기 모드: 기존 데이터로 폼 채우기
        setFormData({
          title: notificationData.title,
          body: notificationData.body,
        });
      } else {
        // 새 작성 모드: 폼 초기화
        setFormData({
          title: '',
          body: ''
        });
      }
      setFormErrors({});
    }
  }, [isOpen, notificationData]);

  // 폼 입력 핸들러
  const handleInputChange = (field: keyof NotificationFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // 에러 초기화
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // 유효성 검사
  const validateForm = (): boolean => {
    const newErrors: Partial<NotificationFormData> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = '제목을 입력해 주세요.';
    } else if (formData.title.length > 100) {
      newErrors.title = '제목은 100자 이내로 입력해 주세요.';
    }
    
    if (!formData.body.trim()) {
      newErrors.body = '내용을 입력해 주세요.';
    } else if (formData.body.length > 1000) {
      newErrors.body = '내용은 1000자 이내로 입력해 주세요.';
    }
    
    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 발송 확인 모달 열기
  const handleSendClick = () => {
    if (validateForm()) {
      setShowConfirmModal(true);
    } else {
      toast.error('필수 항목을 입력해 주세요.');
    }
  };

  // 실제 발송 처리
  const handleConfirmSend = async () => {
    setLoading(true);
    try {
      // 실제 API 호출
      const result = await createPush({
        title: formData.title,
        body: formData.body
      });
      
      if (result) {
        toast.success('알림이 성공적으로 발송되었습니다.');
        setShowConfirmModal(false);
        onSent();
      } else {
        toast.error('알림 발송에 실패했습니다.');
      }
      
    } catch (error) {
      devError('알림 발송 에러:', error);
      toast.error('알림 발송에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 발송 취소
  const handleCancelSend = () => {
    setShowConfirmModal(false);
  };

  // 팝업 닫기
  const handleClose = () => {
    setFormData({ title: '', body: '' });
    setFormErrors({});
    setShowConfirmModal(false);
    onClose();
  };

  const getTitle = () => {
    if (isViewMode) {
      return '알림 상세보기';
    }
    return '새 알림 작성';
  };

  const renderForm = () => (
    <FormContainer>
      {isViewMode && (
        <ReadOnlyContainer>
          <AppTextField
            value={new Date(notificationData!.createAt).toLocaleString()}
            onChange={() => {}}
            label="생성일시"
            readOnly={true}
          />
          <AppTextField
            value={notificationData!.adminName}
            onChange={() => {}}
            label="발송자"
            readOnly={true}
          />
          <AppTextField
            value={notificationData!.createBy}
            onChange={() => {}}
            label="작성자 ID"
            readOnly={true}
          />
        </ReadOnlyContainer>
      )}
      
      <AppTextField
        value={formData.title}
        onChange={(e) => handleInputChange('title', e.target.value)}
        label={isViewMode ? "제목" : "*제목"}
        placeholder="알림 제목을 입력하세요"
        errorMessage={formErrors.title}
        readOnly={isViewMode}
      />

      <AppTextField
        value={formData.body}
        onChange={(e) => handleInputChange('body', e.target.value)}
        label={isViewMode ? "내용" : "*내용"}
        placeholder="알림 내용을 입력하세요"
        errorMessage={formErrors.body}
        readOnly={isViewMode}
        multiline
        height="150px"
      />
    </FormContainer>
  );

  return (
    <>
      <CmsPopup
        title={getTitle()}
        isOpen={isOpen}
        onClose={handleClose}
        width="600px"
        showRequiredMark={!isViewMode}
        rightButtons={[
          ...(isViewMode ? [] : [{
            label: '발송',
            onClick: handleSendClick,
            type: CmsPopupBtnType.PRIMARY,
          }]),
          {
            label: isViewMode ? '닫기' : '취소',
            onClick: handleClose,
            type: CmsPopupBtnType.SECONDARY,
          },
        ]}
      >
        {renderForm()}
      </CmsPopup>
      
      {/* 발송 확인 모달 */}
      <Modal
        isOpen={showConfirmModal}
        onClose={handleCancelSend}
        header="알림 발송 확인"
        body={`작성하신 알림을 발송하시겠습니까?\n발송된 알림은 수정할 수 없습니다.`}
        bgColor="#ffffff"
        zIndex={10002}
        footer={
          <ModalButtonContainer>
            <CmsPopupBtn
              type={CmsPopupBtnType.SECONDARY}
              onClick={handleCancelSend}
              disabled={loading}
            >
              취소
            </CmsPopupBtn>
            <CmsPopupBtn
              type={CmsPopupBtnType.PRIMARY}
              onClick={handleConfirmSend}
              disabled={loading}
            >
              {loading ? '발송 중...' : '발송'}
            </CmsPopupBtn>
          </ModalButtonContainer>
        }
      />
    </>
  );
};

export default NotificationFormPopup;
